import React, { useEffect, useState } from "react";
import Layout from "../../layout/Layout";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSupportKbItems,
  searchSupportKbItems,
  deleteSupportKbItem,
  activateSupportKbItem,
  deactivateSupportKbItem,
  fetchSupportCategories,
  fetchSupportTypes,
  bulkCreateSupportKbItems
} from "../../Redux/Action/SupportKbAction";
import { clearSearchResults } from "../../Redux/Slices/SupportKbSlice";
import LoadingOverlay from "react-loading-overlay-ts";
import { NavLink } from "react-router-dom";
import moment from "moment";
import Pagination from "../../Component/Pagination";
import { Modal, Button, Form } from "react-bootstrap";
import { toast } from "react-toastify";

export default function SupportKbHub() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  // Input state (uncommitted until Enter/submit)
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [audienceFilter, setAudienceFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");

  // Applied/committed filter state used for API calls
  const [appliedSearch, setAppliedSearch] = useState("");
  const [appliedCategory, setAppliedCategory] = useState("ALL");
  const [appliedType, setAppliedType] = useState("ALL");
  const [appliedAudience, setAppliedAudience] = useState("ALL");
  const [appliedStatus, setAppliedStatus] = useState("ALL");

  const [page, setPage] = useState(1);
  const limit = 10;

  // Modals state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deletePermanent, setDeletePermanent] = useState(false);

  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkJsonInput, setBulkJsonInput] = useState("");

  const { items, totalItems, categories, searchResults } = useSelector((state) => state.supportKb);

  const loadData = () => {
    setLoading(true);
    dispatch(fetchSupportCategories());
    dispatch(fetchSupportTypes());

    if (appliedSearch.trim()) {
      dispatch(
        searchSupportKbItems({
          q: appliedSearch,
          category: appliedCategory !== "ALL" ? appliedCategory : undefined,
          type: appliedType !== "ALL" ? appliedType : undefined,
          audience: appliedAudience !== "ALL" ? appliedAudience : undefined
        })
      ).finally(() => setLoading(false));
    } else {
      dispatch(
        fetchSupportKbItems({
          page,
          limit,
          category: appliedCategory !== "ALL" ? appliedCategory : undefined,
          type: appliedType !== "ALL" ? appliedType : undefined,
          audience: appliedAudience !== "ALL" ? appliedAudience : undefined,
          is_active: appliedStatus === "ALL" ? undefined : appliedStatus === "ACTIVE" ? true : false
        })
      ).finally(() => setLoading(false));
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, appliedCategory, appliedType, appliedAudience, appliedStatus, appliedSearch]);

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setAppliedSearch(search);
    setAppliedCategory(categoryFilter);
    setAppliedType(typeFilter);
    setAppliedAudience(audienceFilter);
    setAppliedStatus(statusFilter);
  };

  const handleClearFilters = () => {
    setSearch("");
    setCategoryFilter("ALL");
    setTypeFilter("ALL");
    setAudienceFilter("ALL");
    setStatusFilter("ALL");

    setAppliedSearch("");
    setAppliedCategory("ALL");
    setAppliedType("ALL");
    setAppliedAudience("ALL");
    setAppliedStatus("ALL");
    setPage(1);
    dispatch(clearSearchResults());
  };

  const handleToggleStatus = (item) => {
    setLoading(true);
    const action = item.is_active ? deactivateSupportKbItem(item._id) : activateSupportKbItem(item._id);
    dispatch(action)
      .then(() => loadData())
      .finally(() => setLoading(false));
  };

  const openDeleteModal = (item) => {
    setItemToDelete(item);
    setDeletePermanent(false);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (!itemToDelete) return;
    setLoading(true);
    dispatch(deleteSupportKbItem({ id: itemToDelete._id, permanent: deletePermanent }))
      .then(() => {
        setShowDeleteModal(false);
        setItemToDelete(null);
        loadData();
      })
      .finally(() => setLoading(false));
  };

  const handleBulkImportSubmit = (e) => {
    e.preventDefault();
    try {
      const parsed = JSON.parse(bulkJsonInput);
      const itemsArray = Array.isArray(parsed) ? parsed : [parsed];
      if (itemsArray.length === 0) {
        toast.error("JSON payload is empty");
        return;
      }
      setLoading(true);
      dispatch(bulkCreateSupportKbItems(itemsArray))
        .then((res) => {
          if (!res.error) {
            setShowBulkModal(false);
            setBulkJsonInput("");
            loadData();
          }
        })
        .finally(() => setLoading(false));
    } catch (err) {
      toast.error(`Invalid JSON: Keys and text values must be enclosed in double quotes ("). ${err.message}`);
    }
  };

  const renderStatusBadge = (isActive) => {
    return isActive ? (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium border bg-emerald-100 text-emerald-800 border-emerald-200">
        Active
      </span>
    ) : (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium border bg-secondary-subtle text-secondary border-secondary-subtle">
        Inactive
      </span>
    );
  };

  const rawDisplayList = appliedSearch.trim() ? searchResults : items;

  const displayList = rawDisplayList.filter((item) => {
    if (appliedType !== "ALL" && item.type !== appliedType) {
      return false;
    }
    if (appliedCategory !== "ALL" && item.category !== appliedCategory) {
      return false;
    }
    if (appliedAudience !== "ALL" && item.audience !== appliedAudience) {
      return false;
    }
    return true;
  });

  return (
    <Layout>
      <LoadingOverlay active={loading} spinner text="Processing Support Knowledge Base...">
        <div className="dashboard_data_wrap">
          <div className="dashbord_cont_wrapper">
            <div className="modern-user-section-card" style={{ background: "#fff", padding: 24, borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
              <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                  <h4 className="mb-0" style={{ color: "#034328", fontWeight: 700 }}>Support Knowledge Base</h4>
                  <p className="text-muted small mb-0">Manage AI Support Chat context, policies, workflows, FAQs, and business rules.</p>
                </div>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-outline-success d-flex align-items-center"
                    onClick={() => setShowBulkModal(true)}
                  >
                    <i className="fas fa-file-upload me-2"></i> Bulk Import
                  </button>
                  <NavLink to="/support-knowledge-base/contribute" className="common-btn no-underline d-flex align-items-center">
                    <i className="fas fa-plus-circle me-2"></i> New Knowledge Item
                  </NavLink>
                </div>
              </div>

              {/* Search & Filters Form (Applies on Enter / Submit) */}
              <form onSubmit={handleFilterSubmit} className="row g-3 mb-4 p-3 bg-light rounded-lg border border-gray-100">
                <div className="col-md-3">
                  <div className="input-group shadow-sm">
                    <span className="input-group-text bg-white border-end-0">
                      <i className="fas fa-search text-muted"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0 focus-ring-none"
                      placeholder="Search KB items..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                </div>

                <div className="col-md-2">
                  <select
                    className="form-select shadow-sm"
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  >
                    <option value="ALL">All Categories</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-2">
                  <select
                    className="form-select shadow-sm"
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                  >
                    <option value="ALL">All Types</option>
                    <option value="document">Document</option>
                    <option value="faq">FAQ</option>
                    <option value="business_rule">Business Rule</option>
                    <option value="workflow">Workflow</option>
                    <option value="policy">Policy</option>
                    <option value="service">Service</option>
                    <option value="support">Support</option>
                    <option value="configuration">Configuration</option>
                  </select>
                </div>

                <div className="col-md-2">
                  <select
                    className="form-select shadow-sm"
                    value={audienceFilter}
                    onChange={(e) => setAudienceFilter(e.target.value)}
                  >
                    <option value="ALL">All Audiences</option>
                    <option value="public">Public</option>
                    <option value="care_seeker">Care Seeker</option>
                    <option value="carer">Carer</option>
                    <option value="partner">Partner</option>
                    <option value="agent">Agent</option>
                    <option value="admin">Admin</option>
                    <option value="internal">Internal</option>
                  </select>
                </div>

                <div className="col-md-3 d-flex gap-2">
                  <select
                    className="form-select shadow-sm"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="ALL">All Status</option>
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                  <button type="submit" className="btn btn-emerald text-white px-3" style={{ background: "#034328" }} title="Apply Filters">
                    <i className="fas fa-search"></i>
                  </button>
                  {(appliedSearch.trim() || appliedCategory !== "ALL" || appliedType !== "ALL" || appliedAudience !== "ALL" || appliedStatus !== "ALL") && (
                    <button type="button" className="btn btn-outline-secondary px-3" onClick={handleClearFilters} title="Clear Filters">
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              </form>

              {/* Table */}
              <div className="table-responsive">
                <table className="table table-hover align-middle border-top border-gray-100">
                  <thead className="bg-light">
                    <tr>
                      <th className="px-3">Title & Summary</th>
                      <th>Type</th>
                      <th>Category</th>
                      <th>Audience</th>
                      <th>Priority</th>
                      <th>Status</th>
                      <th>Updated</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="border-top-0">
                    {displayList.map((item) => {
                      const isActive = item.is_active !== undefined ? item.is_active : true;
                      const updatedAt = item.updatedAt || item.createdAt || item.metadata?.updatedAt || item.metadata?.createdAt;

                      return (
                      <tr key={item._id} className="transition-all hover:bg-emerald-50/30">
                        <td className="px-3" style={{ maxWidth: 300 }}>
                          <div style={{ fontWeight: 600, color: "#111" }}>{item.title}</div>
                          {item.short_answer && (
                            <div className="text-muted small text-truncate" title={item.short_answer}>
                              {item.short_answer}
                            </div>
                          )}
                        </td>
                        <td>
                          <span className="badge bg-light text-dark border text-uppercase" style={{ fontSize: "0.7rem" }}>
                            {item.type}
                          </span>
                        </td>
                        <td className="small">{item.category}</td>
                        <td>
                          <span className="badge bg-info-subtle text-info border border-info-subtle" style={{ fontSize: "0.7rem" }}>
                            {item.audience}
                          </span>
                        </td>
                        <td className="fw-bold">{item.priority || 0}</td>
                        <td>{renderStatusBadge(isActive)}</td>
                        <td className="small text-muted">{updatedAt ? moment(updatedAt).format("DD MMM YYYY") : "-"}</td>
                        <td>
                          <div className="d-flex justify-content-center gap-2 align-items-center">
                            <div className="form-check form-switch m-0" title={isActive ? "Deactivate" : "Activate"}>
                              <input
                                className="form-check-input cursor-pointer"
                                type="checkbox"
                                checked={isActive}
                                onChange={() => handleToggleStatus({ ...item, is_active: isActive })}
                              />
                            </div>
                            <NavLink to={`/support-knowledge-base/edit/${item._id}`} className="btn btn-sm btn-outline-primary shadow-sm" title="Edit Item">
                              <i className="fas fa-pencil"></i>
                            </NavLink>
                            <button className="btn btn-sm btn-outline-danger shadow-sm" onClick={() => openDeleteModal(item)} title="Delete / Deactivate">
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                    {displayList.length === 0 && (
                      <tr>
                        <td colSpan="8" className="text-center py-5 text-muted">
                          <i className="fas fa-search-minus fa-3x mb-3 opacity-20"></i>
                          <p>No support knowledge base items found.</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {!appliedSearch.trim() && totalItems > 0 && (
                <div className="mt-4 border-top pt-4">
                  <Pagination
                    handlePageClick={(e) => setPage(e.selected + 1)}
                    pageCount={Math.ceil(totalItems / limit)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </LoadingOverlay>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger h5">Delete Support Knowledge Item</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete <strong>{itemToDelete?.title}</strong>?
          </p>
          <Form.Check
            type="checkbox"
            id="permanent-delete-support"
            label="Permanent Deletion (Remove entirely from Database & Vector Index)"
            checked={deletePermanent}
            onChange={(e) => setDeletePermanent(e.target.checked)}
            className="text-danger fw-medium mt-3"
          />
          {!deletePermanent && (
            <p className="text-muted small mt-2 mb-0">
              By default, this will soft-deactivate the item so it is excluded from AI retrieval without losing historical records.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Confirm Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bulk Import Modal */}
      <Modal show={showBulkModal} onHide={() => setShowBulkModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ color: "#034328", fontWeight: 700 }}>Bulk Import Support Knowledge Items</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <p className="text-muted small mb-0">
              Paste a JSON array of support knowledge base objects conforming to the schema (must include <code>title</code>, <code>content</code>, <code>category</code>, etc.).
            </p>
            <Button
              variant="outline-secondary"
              size="sm"
              type="button"
              onClick={() =>
                setBulkJsonInput(`[
  {
    "title": "Example Support FAQ",
    "category": "Billing",
    "type": "faq",
    "audience": "public",
    "content": "<p>FAQ content here...</p>",
    "short_answer": "Quick summary"
  }
]`)
              }
            >
              <i className="fas fa-file-code me-1"></i> Fill Template
            </Button>
          </div>
          <Form onSubmit={handleBulkImportSubmit}>
            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={10}
                placeholder={`[
  {
    "title": "Example Support FAQ",
    "category": "Billing",
    "type": "faq",
    "audience": "public",
    "content": "<p>FAQ content here...</p>",
    "short_answer": "Quick summary"
  }
]`}
                value={bulkJsonInput}
                onChange={(e) => setBulkJsonInput(e.target.value)}
                required
                style={{ fontFamily: "monospace", fontSize: "0.85rem" }}
              />
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShowBulkModal(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="success" style={{ background: "#034328" }}>
                Import Batch
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Layout>
  );
}
